import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { subscribeMyParticipantsForUid } from '../services/messages';
import '../styles/BottomNav.css';

const HomeIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const MailIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const MenuIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const FeedIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="7" x2="19" y2="7" />
    <line x1="5" y1="12" x2="19" y2="12" />
    <line x1="5" y1="17" x2="15" y2="17" />
  </svg>
);

const NAV_ROUTES = ['/dashboard', '/feed', '/create-duck', '/messages'];
const GUEST_NAV_ROUTES = ['/', '/view-ducks', '/signup', '/login'];

function BottomNav({ user, onToggleMenu }) {
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      return;
    }
    const unsub = subscribeMyParticipantsForUid(user.uid, (participants) => {
      const list = Object.values(participants || {});
      const total = list.reduce(
        (sum, entry) => sum + Math.max(0, Number(entry?.unreadCount || 0)),
        0
      );
      setUnreadCount(total);
    });
    return () => {
      try { unsub(); } catch (_) { /* cleanup */ }
    };
  }, [user]);

  if (!user) {
    const isGuestMoreActive = !GUEST_NAV_ROUTES.some((r) => (
      r === '/' ? location.pathname === '/' : location.pathname.startsWith(r)
    ));

    return (
      <nav className="bottom-nav bottom-nav--guest" aria-label="Guest bottom navigation">
        <NavLink
          to="/"
          className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
        >
          <span className="bottom-nav-icon"><HomeIcon /></span>
          <span className="bottom-nav-label">Home</span>
        </NavLink>

        <NavLink
          to="/view-ducks"
          className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
        >
          <span className="bottom-nav-icon" aria-hidden="true"><FeedIcon /></span>
          <span className="bottom-nav-label">Pond</span>
        </NavLink>

        <NavLink
          to="/signup"
          className={({ isActive }) => `bottom-nav-item bottom-nav-center${isActive ? ' active' : ''}`}
        >
          <span className="bottom-nav-center-btn" aria-hidden="true">+</span>
          <span className="bottom-nav-label">Sign Up</span>
        </NavLink>

        <NavLink
          to="/login"
          className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
        >
          <span className="bottom-nav-icon"><MailIcon /></span>
          <span className="bottom-nav-label">Log In</span>
        </NavLink>

        <button
          type="button"
          className={`bottom-nav-item bottom-nav-more${isGuestMoreActive ? ' active' : ''}`}
          onClick={onToggleMenu}
          aria-label="Open menu"
        >
          <span className="bottom-nav-icon"><MenuIcon /></span>
          <span className="bottom-nav-label">More</span>
        </button>
      </nav>
    );
  }

  const isMoreActive = !NAV_ROUTES.some((r) => location.pathname.startsWith(r));

  return (
    <nav className="bottom-nav" aria-label="Bottom navigation">
      <NavLink
        to="/dashboard"
        className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
      >
        <span className="bottom-nav-icon"><HomeIcon /></span>
        <span className="bottom-nav-label">Home</span>
      </NavLink>

      <NavLink
        to="/feed"
        className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
      >
        <span className="bottom-nav-icon" aria-hidden="true"><FeedIcon /></span>
        <span className="bottom-nav-label">Feed</span>
      </NavLink>

      <NavLink
        to="/create-duck"
        className={({ isActive }) => `bottom-nav-item bottom-nav-center${isActive ? ' active' : ''}`}
      >
        <span className="bottom-nav-center-btn" aria-hidden="true">+</span>
        <span className="bottom-nav-label">Hatch</span>
      </NavLink>

      <NavLink
        to="/messages"
        className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
      >
        <span className="bottom-nav-icon">
          <MailIcon />
          {unreadCount > 0 && (
            <span className="bottom-nav-badge">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </span>
        <span className="bottom-nav-label">Inbox</span>
      </NavLink>

      <button
        type="button"
        className={`bottom-nav-item bottom-nav-more${isMoreActive ? ' active' : ''}`}
        onClick={onToggleMenu}
        aria-label="Open menu"
      >
        <span className="bottom-nav-icon"><MenuIcon /></span>
        <span className="bottom-nav-label">More</span>
      </button>
    </nav>
  );
}

export default BottomNav;
